import { useVLEInteractions } from "@/hooks/useVLEInteractions";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Eye, 
  Phone, 
  MessageCircle, 
  Mail, 
  Navigation, 
  TrendingUp,
  BarChart3,
} from "lucide-react";

export function VLEAnalyticsDashboard() {
  const { tenant } = useAuth();
  const { 
    statsLoading, 
    profileViews, 
    getStatByType,
    totalInteractions,
    monthlyInteractions,
  } = useVLEInteractions(tenant?.id);

  if (statsLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const statCards = [
    {
      label: "Profile Views",
      value: profileViews || 0,
      icon: Eye,
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
    },
    {
      label: "Calls",
      value: getStatByType("call")?.total_count || 0,
      monthValue: getStatByType("call")?.this_month_count || 0,
      icon: Phone,
      color: "text-green-500",
      bgColor: "bg-green-500/10",
    },
    {
      label: "WhatsApp",
      value: getStatByType("whatsapp")?.total_count || 0,
      monthValue: getStatByType("whatsapp")?.this_month_count || 0,
      icon: MessageCircle,
      color: "text-emerald-500",
      bgColor: "bg-emerald-500/10",
    },
    {
      label: "Emails",
      value: getStatByType("email")?.total_count || 0,
      monthValue: getStatByType("email")?.this_month_count || 0,
      icon: Mail,
      color: "text-orange-500",
      bgColor: "bg-orange-500/10",
    },
    {
      label: "Directions",
      value: getStatByType("map")?.total_count || 0,
      monthValue: getStatByType("map")?.this_month_count || 0,
      icon: Navigation,
      color: "text-purple-500",
      bgColor: "bg-purple-500/10",
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5" />
          Profile Analytics
        </CardTitle>
        <CardDescription>
          Track how customers interact with your public profile
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 rounded-lg border bg-muted/30">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <TrendingUp className="h-4 w-4" />
              Total Interactions
            </div>
            <p className="text-2xl font-bold">{totalInteractions}</p>
          </div>
          <div className="p-4 rounded-lg border bg-muted/30">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <TrendingUp className="h-4 w-4" />
              This Month
            </div>
            <p className="text-2xl font-bold">{monthlyInteractions}</p>
          </div>
        </div>

        {/* Detailed Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {statCards.map((stat) => (
            <div 
              key={stat.label}
              className="p-4 rounded-lg border flex flex-col items-center text-center"
            >
              <div className={`p-3 rounded-full ${stat.bgColor} mb-2`}>
                <stat.icon className={`h-5 w-5 ${stat.color}`} />
              </div>
              <p className="text-2xl font-bold">{stat.value}</p>
              <p className="text-xs text-muted-foreground">{stat.label}</p>
              {stat.monthValue !== undefined && stat.monthValue > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  {stat.monthValue} this month
                </p>
              )}
            </div>
          ))}
        </div>

        {!tenant?.is_public && (
          <p className="text-sm text-muted-foreground text-center py-4 border-t">
            Publish your profile to start tracking visitor interactions
          </p>
        )}
      </CardContent>
    </Card>
  );
}
